import { useAuth } from '../context/AuthContext';
import { useEffect } from 'react';

interface Permission {
  view_dashboard: boolean;
  manage_orders: boolean;
  manage_products: boolean;
  manage_customers: boolean;
  reports_access: boolean;
  billing_access: boolean;
  manage_team: boolean;
  integrations_access: boolean;
  scaleboard_access: boolean;
  support_access: boolean;
  isight_access: boolean;
  settings_access: boolean;
}

export function usePermissions(): Permission {
  const authContext = useAuth();
  
  if (!authContext) {
    return {
      view_dashboard: false,
      manage_orders: false,
      manage_products: false,
      manage_customers: false,
      reports_access: false,
      billing_access: false,
      manage_team: false,
      integrations_access: false,
      scaleboard_access: false,
      support_access: false,
      isight_access: false,
      settings_access: false,
    };
  }

  const { user, stores, currentShop } = authContext;
  
  // If no user or no current shop, no permissions
  if (!user || !currentShop) {
    return {
      view_dashboard: false,
      manage_orders: false,
      manage_products: false,
      manage_customers: false,
      reports_access: false,
      billing_access: false,
      manage_team: false,
      integrations_access: false,
      scaleboard_access: false,
      support_access: false,
      isight_access: false,
      settings_access: false,
    };
  }

  // Find current store
  const currentStore = stores.find(s => s.shop === currentShop);
  
  if (!currentStore) {
    return {
      view_dashboard: false,
      manage_orders: false,
      manage_products: false,
      manage_customers: false,
      reports_access: false,
      billing_access: false,
      manage_team: false,
      integrations_access: false,
      scaleboard_access: false,
      support_access: false,
      isight_access: false,
      settings_access: false,
    };
  }

  // Get user's role in this store
  const userRole = currentStore.user_role || 'member';
  
  // Define permissions based on role
  switch (userRole) {
    case 'owner':
    case 'admin':
      return {
        view_dashboard: true,
        manage_orders: true,
        manage_products: true,
        manage_customers: true,
        reports_access: true,
        billing_access: true,
        manage_team: true,
        integrations_access: true,
        scaleboard_access: true,
        support_access: true,
        isight_access: true,
        settings_access: true,
      };
      
    case 'manager':
      return {
        view_dashboard: true,
        manage_orders: true,
        manage_products: true,
        manage_customers: true,
        reports_access: true,
        billing_access: false,
        manage_team: true,
        integrations_access: false,
        scaleboard_access: true,
        support_access: true,
        isight_access: true,
        settings_access: true,
      };
      
    case 'member':
    default:
      return {
        view_dashboard: true,
        manage_orders: true,
        manage_products: true,
        manage_customers: true,
        reports_access: true,
        billing_access: false,
        manage_team: false,
        integrations_access: false,
        scaleboard_access: false,
        support_access: false,
        isight_access: false,
        settings_access: false,
      };
  }
}

export function canAccessFeature(feature: keyof Permission): boolean {
  const permissions = usePermissions();
  return permissions[feature] || false;
}

export function useRole(): string {
  const { stores, currentShop } = useAuth();
  
  if (!currentShop) {
    return 'member';
  }

  const currentStore = stores.find((s: any) => s.shop === currentShop);
  return currentStore?.user_role || 'member';
}
